import React, { useEffect } from 'react';
import { Sun, Moon, MapPin, Hash, Users, Utensils, Clock, X, ExternalLink } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const BookingDetailModal = ({ event, onClose, onViewLead, t, currentUser, leads = [] }) => {
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (!event) return null;

    const startDate = new Date(event.eventDate || event.startTime);
    const dateStr = formatDate(startDate, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
    const shift = (event.shift || 'Day').charAt(0).toUpperCase() + (event.shift || 'Day').slice(1).toLowerCase();
    const isDay = shift === 'Day' || shift === 'Morning';

    const leadId = event.crmId || (event.source !== 'banquet' ? event.id : null);
    const targetLead = leads.find(l => String(l.id) === String(leadId) || `local-tentative-${l.id}` === String(leadId));

    let isAuthorizedToView = false;
    if (leadId && currentUser) {
        const userRole = currentUser.role?.toLowerCase();
        if (['admin', 'owner', 'finance'].includes(userRole)) {
            isAuthorizedToView = true;
        } else {
            if (targetLead && String(targetLead.assignedTo) === String(currentUser.id)) {
                isAuthorizedToView = true;
            }
        }
    }

    // Shift pill colours
    const shiftCls = isDay
        ? 'bg-amber-100 text-amber-700 border border-amber-200'
        : 'bg-indigo-100 text-indigo-700 border border-indigo-200';

    return (
        <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[95] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh] animate-scale-in overflow-hidden">

                {/* ── Header ── */}
                <div className={`border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 sticky top-0 z-10 ${event.status === 'tentative' ? 'bg-orange-50/80' : 'bg-white'}`}>
                    <div className="min-w-0 flex-1">
                        {/* Icon + title row */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className={`p-1.5 rounded-lg ${isDay ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                                {isDay
                                    ? <Sun className="w-4 h-4 text-amber-600" />
                                    : <Moon className="w-4 h-4 text-indigo-600" />
                                }
                            </div>
                            <h2 className="text-base sm:text-lg font-bold text-gray-900">
                                {event.status === 'tentative' ? (t('tentativeBookingDetails') || 'Tentative Booking') : (t('bookingDetails') || 'Booking Details')}
                            </h2>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${shiftCls}`}>
                                {t(shift.toLowerCase() + 'Shift') || (shift + ' Shift')}
                            </span>
                            {event.status === 'tentative' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-orange-100 text-orange-700 border border-orange-200 animate-pulse">
                                    {t('tentative') || 'TENTATIVE'}
                                </span>
                            )}
                        </div>

                        {/* Client name */}
                        <p className="font-semibold text-gray-800 text-sm mt-1.5 truncate">
                            {event.clientName || 'Client'}
                        </p>

                        {/* Meta row */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            {(event.functionName || event.title) && (
                                <span className="text-xs text-gray-500">{event.functionName || event.title}</span>
                            )}
                            {event.venue && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                    <MapPin className="w-3 h-3" />{event.venue}
                                </span>
                            )}
                            {event.orderNo && (
                                <span className="flex items-center gap-1 text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    <Hash className="w-3 h-3" />{event.orderNo}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full shrink-0 transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto bg-slate-50">
                    <div className="p-4 sm:p-5 space-y-3">

                        {/* Date card */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('eventDate') || 'Event Date'}</p>
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${isDay ? 'bg-amber-50 border border-amber-200' : 'bg-indigo-50 border border-indigo-200'}`}>
                                    <span className={`text-[10px] font-bold leading-none ${isDay ? 'text-amber-500' : 'text-indigo-500'}`}>
                                        {formatDate(startDate, { month: 'short' }).toUpperCase()}
                                    </span>
                                    <span className={`text-lg font-black leading-tight ${isDay ? 'text-amber-700' : 'text-indigo-700'}`}>
                                        {startDate.getDate()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{dateStr}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">{t(shift.toLowerCase() + 'Shift') || (shift + ' Shift')} · Banquet</p>
                                </div>
                            </div>
                        </div>

                        {/* Details card */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{t('details') || 'Details'}</p>

                            {/* Venue */}
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-4 h-4 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{event.venue || 'TBD'}</p>
                                    <p className="text-xs text-gray-400">{t('venue') || 'Venue'}</p>
                                </div>
                            </div>

                            {/* Guests */}
                            {event.noOfGuests && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">{event.noOfGuests} {t('guests') || 'Guests'}</p>
                                        <p className="text-xs text-gray-400">{t('expectedAttendance') || 'Expected Attendance'}</p>
                                    </div>
                                </div>
                            )}

                            {/* Caterers */}
                            {event.caterers && event.caterers !== '-' && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        <Utensils className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{event.caterers}</p>
                                        <p className="text-xs text-gray-400">{t('caterers') || 'Caterers'}</p>
                                    </div>
                                </div>
                            )}

                            {/* Setup */}
                            {event.setupName && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">{event.setupName}</p>
                                        <p className="text-xs text-gray-400">{t('setup') || 'Setup'}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* CRM link */}
                        {event.crmId && (
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex flex-col gap-2 mt-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                                    <p className="text-xs text-blue-700 font-semibold flex-1">
                                        {t('linkedCrmLead') || 'Linked CRM Lead'} #{event.crmId}
                                    </p>
                                </div>
                                {targetLead?.assignee?.username && (
                                    <div className="flex items-center gap-1.5 ml-4">
                                        <Users className="w-3.5 h-3.5 text-blue-400" />
                                        <span className="text-[11px] text-blue-600 font-medium">
                                            {t('assignedTo')}: <span className="font-bold">{targetLead.assignee.username}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        {t('close') || 'Close'}
                    </button>
                    {event.crmId && isAuthorizedToView && (
                        <button
                            onClick={() => { onClose(); setTimeout(() => onViewLead(event), 200); }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors active:scale-95 shadow-sm"
                        >
                            <ExternalLink className="w-4 h-4" strokeWidth={2} />
                            {t('viewCrmLead') || 'View CRM Lead'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingDetailModal;
